//import core libraries
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";

//import third party libraries
import socketIO from "socket.io-client";
import { useMediaQuery } from "react-responsive";
import { twMerge } from "tailwind-merge";
import { IoSearch } from "react-icons/io5";
import { IoClose } from "react-icons/io5";
import { FaRegMessage } from "react-icons/fa6";
import { FaCheck, FaDownload } from "react-icons/fa";
import { HiMenu } from "react-icons/hi";
import { IoMdArrowRoundBack } from "react-icons/io";

//importing custom hooks
import { useThunk } from "../../hooks/useThunk.js";

// import constant data
import { orderStatus, roles } from "../../constants/commonConstants.js";

//import assets
import logo from "../../assets/Sidebar/Logo_short.png";
import waiting from "../../assets/Thread/waiting.svg";
import waiting_two from "../../assets/Thread/waiting2.svg";
import ClipLoader from "react-spinners/ClipLoader";

//import components
import TutorialCard from "../../components/Thread/TutorialCard";
import ArrowButton from "../../components/Form/ArrowButton";
import MessageList from "../../components/Thread/MessageList.js";
import CountdownTimer from "../../components/Thread/CountDownTimer.jsx";
import OrderDetails from "../../components/Thread/OrderDetails.jsx";
import TrackOrder from "../../components/Thread/TrackOrder.jsx";
import AssignedWriterProfileCard from "../../components/Thread/AssignedWriterProfileCard.jsx";
import { canTipWriter } from "../../utils/writerTipEligibility.js";

//import dispatchers, thunks, functions, utills
import {
  clearThreads,
  setSelectedThreadId,
  setSelectedThreadRoomId,
  setSelectedFormType,
  setSelectedThread,
  setSelectedOrder,
  clearSelectedThreadAndOrder,
  setThreads,
  setSelectedPage,
  fetchUserThreads,
  fetchUserOrderByRoomId,
  clearThreadChats,
  fetchThreadById,
  setBulkThreads,
  setThreadBulkChats,
  updateThreadMessage,
  removeThreadMessage,
} from "../../store";
import { getTokenPayload } from "../../utils/getTokenPayload";
import showToast from "../../utils/toastNotifications.js";
import { getUnreadMessagesCount } from "../../utils/getUnreadMessagesCount.js";
import { useNavigate } from "react-router-dom";

// extracrting current environment and it's properties
import { CONFIGURATIONS } from "../../config/envConfig.js";
import { currencyFormatter, formatOrderStatus } from "../../utils/formatting.js";

// client socket
const socket = socketIO.connect(CONFIGURATIONS.API_BASE_DOMAIN, {
  auth: {
    token: localStorage.getItem("token"),
  },
});

// const values
const SIZE = 5;
const getParticipantId = (participant) => participant?._id?.toString() || participant?.toString() || "";

const PresenceBadge = ({ label, isOnline }) => (
  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#d9e3f4] bg-white px-2.5 py-1 font-inter text-[11px] font-semibold leading-4 text-secondary">
    <span className={`h-2 w-2 rounded-full ${isOnline ? "bg-[#16a34a]" : "bg-gray-300"}`} />
    {label} {isOnline ? "Online" : "Offline"}
  </span>
);

const isWriterAssignedToOrder = (order) => Boolean(
  order?.writer?._id || order?.writer?.id || (typeof order?.writer === "string" && order.writer)
);

const getDeliveryFileType = (file) =>
  typeof file === "string" ? "final_file" : file?.fileType || "additional_file";

const getLatestDeliverables = (order = null) => {
  const completedRevisions = order?.revisions?.filter(
    (revision) => revision.status === "COMPLETED" && revision.finalDeliverables?.length > 0
  ) || [];

  if (completedRevisions.length > 0) {
    return completedRevisions[completedRevisions.length - 1]?.finalDeliverables || [];
  }

  return order?.finalDeliverables || [];
};

const hasEditorReviewDeliverable = (order = null) =>
  getLatestDeliverables(order).some((file) => getDeliveryFileType(file) === "editor_review");

const getCustomerOrderStatusLabel = (order) => {
  if (
    order?.status === orderStatus.PENDING_WRITER ||
    (order?.status === orderStatus.ORDER_IN_PROGRESS && !isWriterAssignedToOrder(order))
  ) {
    return "Assigning expert";
  }

  return formatOrderStatus(order?.status);
};

const getOrderStatusSummary = (order) => {
  const status = order?.status;

  if (status === orderStatus.PENDING_WRITER || (status === orderStatus.ORDER_IN_PROGRESS && !isWriterAssignedToOrder(order))) {
    return "We are matching your order with the right expert. You will be notified once a writer is assigned.";
  }

  switch (status) {
    case orderStatus.PENDING_PAYMENT:
      return "Payment is required before work can begin.";
    case orderStatus.PENDING_WRITER:
      return "We are assigning an expert to your order.";
    case orderStatus.ORDER_IN_PROGRESS:
      return "Your writer is working on this order now.";
    case orderStatus.ORDER_DELIVERED:
      return "Your order has been delivered for review.";
    case orderStatus.IN_REVISION:
      return "Your revision is in progress.";
    case orderStatus.COMPLAINT_RAISED:
      return "Your complaint is being reviewed.";
    case orderStatus.COMPLETED:
      return "This order is completed.";
    case orderStatus.RESOLVED:
      return "This order has been resolved.";
    default:
      return "";
  }
};

const Thread = () => {

  const location = useLocation();
  const tokenPayload = getTokenPayload();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); //param1 = searchParams.get('param1');
  const thread = useSelector((state) => state.thread);
  const { orders } = useSelector((state) => state.createOrder?.userOrders);
  const orderStatusSummary = getOrderStatusSummary(thread?.selectedOrder);
  const customerOrderStatusLabel = getCustomerOrderStatusLabel(thread?.selectedOrder);
  const isWriterAssigned = isWriterAssignedToOrder(thread?.selectedOrder);
  const [threadPresence, setThreadPresence] = useState({
    customer: { userId: "", isOnline: false },
    writer: { userId: "", isOnline: false },
  });
  const selectedWriterId = getParticipantId(thread?.selectedThread?.writer);
  const isSelectedWriterOnline = threadPresence?.writer?.userId === selectedWriterId && threadPresence?.writer?.isOnline;

  const roomIdSearchParam = searchParams.get("roomIdParam") || null;
  const threadIdSearchParam = searchParams.get("threadIdParam") || null;
  const paymentStatusSearchParam = searchParams.get("paymentStatus") || searchParams.get("status") || null;
  const paymentPurposeSearchParam = searchParams.get("paymentPurpose") || null;
  const paymentAmountSearchParam = searchParams.get("amount") || null;
  const extraPaymentNotice = paymentPurposeSearchParam === "extra-payment" && paymentStatusSearchParam
    ? {
        success: { text: "Your extra payment was completed successfully and has been added to this order.", classes: "border-emerald-200 bg-emerald-50 text-emerald-800" },
        cancelled: { text: "The extra payment was cancelled. No payment was recorded for this attempt.", classes: "border-amber-200 bg-amber-50 text-amber-800" },
        failed: { text: "We could not complete the extra payment. Please review the order and try again.", classes: "border-red-200 bg-red-50 text-red-800" },
      }[paymentStatusSearchParam]
    : null;

  useEffect(() => {
    if (paymentStatusSearchParam === "success" && paymentPurposeSearchParam === "tip") {
      const formattedAmount = paymentAmountSearchParam
        ? currencyFormatter.format(parseFloat(paymentAmountSearchParam) || 0)
        : "";
      showToast("success", `Thank you for tipping the writer${formattedAmount ? ` ${formattedAmount}` : ""}.`);
    }
  }, [paymentStatusSearchParam, paymentPurposeSearchParam, paymentAmountSearchParam]);

  //thread pagination model
  const [pagination, setPagination] = useState({
    page: Math.floor(thread.threads?.length / SIZE + 1) || 1,
    size: SIZE,
  });
  const [totalNumOfRecords, setTotalNumOfRecords] = useState(0);

  //states for manage dynamic screen behaviours
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const isLargeScreen = useMediaQuery({ query: "(min-width: 1024px)" });
  const [isScreenSmall, setIsScreenSmall] = useState(window.innerWidth >= 360);
  const [isScreenMedium, setIsScreenMedium] = useState(
    window.innerWidth >= 640
  );
  const [isScreenLarge, setIsScreenLarge] = useState(window.innerWidth >= 1440);

  /*
   * ============================= CHATTING RELATED REFS =========================
   *
   * definig several refes parallay to several local/global states due to socket listeners not getting the latest updated
   *  values of states and resulting unpredicte behaviours
   */

  const userIdRef = useRef(tokenPayload?._id || "");
  const roleRef = useRef(
    tokenPayload?.roles ? tokenPayload?.roles[0] : roles.USER
  );
  const selectedThreadRef = useRef(thread?.selectedThread);
  const threasdsRef = useRef(thread?.threads);
  const chatRef = useRef(thread?.selectedThreadChat);
  // const isTypingRef = useRef(isTyping);
  // const typingUsersRef = useRef(typingUsers);
  // const mentionUsersRef = useRef(mentionUsers);
  // const selectedMentionUserRef = useRef(selectedMentionUser);
  // const currentMentionedUsersRef = useRef(currentMentionedUsers);

  //invoking custom hooks
  const [doFetchUserThreads, isFetchingUserThreads, fetchUserThreadsError] =
    useThunk(fetchUserThreads);
  const [
    doFetchUserOrderByRoomId,
    isFetchUserOrderByRoomId,
    fetchUserOrderByRoomIdError,
  ] = useThunk(fetchUserOrderByRoomId);
  const [doFetchThreadById, isFetchThreadById, fetchThreadByIdError] =
    useThunk(fetchThreadById);

  /*
  *
  * =====================  SIDE EFFECTS DEFINITIONS  =============================================================================
  *
  */

  // defining event listeners
  useEffect(() => {
    //handle screen size resize event listeners
    const handleResize = () => {
      setIsScreenLarge(window.innerWidth >= 1440);
      setIsScreenMedium(window.innerWidth >= 640);
      setIsScreenSmall(window.innerWidth >= 360);
    };

    //clear redux storage - threads properties
    const handleUnload = () => {

      // disconnectiong the socket connection before leave from the page/tab/browser
      if (socket.connected) {
        socket.disconnect();
      }
      dispatch(clearThreads());
      dispatch(clearSelectedThreadAndOrder());
    };

    // invoking methods based on if query parameters exists or if query parametrs does not exist
    // query paramerts exists if this thread page loaded from my order table : view order selected
    // query parametrs does not exist if this thread page loaded as general option
    if (roomIdSearchParam && threadIdSearchParam) {
      refetchSelectedThreadAndOrderFromQueryParams();
    } else {
      refetchSelectedThreadAndOrder();
    }

    // triggered when user leaves/close browser, tab or navigates away
    window.addEventListener("beforeunload", handleUnload);
    // detect screen sizes
    window.addEventListener("resize", handleResize);

    // cleanup functions
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // fetch user threads accroding to paginatin modal
  useEffect(() => {
    // invoking for fetch user threads
    if (
      thread.threads?.length % SIZE === 0 &&
      !thread?.selectedPage &&
      !roomIdSearchParam &&
      !threadIdSearchParam
    ) {
      fetchAllUserThreads();
    }
  }, [pagination]);

  /*
   *
   * =====================  CHATTING RELATED SIDE EFFECTS DEFINITIONS  =============================================================================
   *
   */

  // client socket join with their each associated rooms
  useEffect(() => {
    const joinRooms = () => {
      if (
        (thread?.threads?.length > 0 ||
          (roomIdSearchParam && threadIdSearchParam)) &&
        tokenPayload?._id != ""
      ) {
        socket.emit("join-room", {
          userId: tokenPayload?._id || "",
          role: tokenPayload?.roles ? tokenPayload?.roles[0] : "",
          brand: CONFIGURATIONS.CLIENT_APP_BRAND,
        });
      }
    };
    const handleJoinRoomResponse = () => { };

    socket.auth = { token: localStorage.getItem('token') };
    socket.on("connect", joinRooms);
    socket.on("joinRoomResponse", handleJoinRoomResponse);

    if (socket.connected) {
      joinRooms();
    } else {
      socket.connect();
    }

    return () => {
      socket.off("connect", joinRooms);
      socket.off("joinRoomResponse", handleJoinRoomResponse);
    };
  }, [thread?.threads, roomIdSearchParam, threadIdSearchParam]);

  // emiting events for track who are the active viewing users of threads at this moment
  useEffect(() => {
    if (thread?.selectedThread?._id) {
      socket.emit("open-thread", {
        userId: tokenPayload?._id || "",
        roomId: thread?.selectedThread?._id || "",
      });

      // Handler for closing thread on refresh or tab/browser close
      const handleUnload = () => {
        socket.emit("close-thread", {
          userId: tokenPayload?._id || "",
          roomId: thread?.selectedThread?._id || "",
        });
      };

      // Listen to beforeunload event for cleanup on refresh or close
      window.addEventListener("beforeunload", handleUnload);

      // Cleanup
      return () => {
        window.removeEventListener("beforeunload", handleUnload);
        socket.emit("close-thread", {
          userId: tokenPayload?._id || "",
          roomId: thread?.selectedThread?._id || "",
        });
      };
    }
  }, [thread?.selectedThread?._id]);

  useEffect(() => {
    if( thread?.selectedFormType === "system" && thread?.selectedThread?._id){
      socket.emit("seen-notifications", thread?.selectedThread?._id)
    }
  },[thread?.selectedThread?._id, thread?.selectedFormType])

  useEffect(() => {
    if (!thread?.selectedThread?._id) {
      setThreadPresence({
        customer: { userId: "", isOnline: false },
        writer: { userId: "", isOnline: false },
      });
      return;
    }

    socket.emit("presence:get-thread", { threadId: thread?.selectedThread?._id });

    const handleThreadPresence = (presence) => {
      if (presence?.threadId?.toString() !== thread?.selectedThread?._id?.toString()) return;
      setThreadPresence({
        customer: presence?.customer || { userId: "", isOnline: false },
        writer: presence?.writer || { userId: "", isOnline: false },
      });
    };

    const handlePresenceChanged = ({ userId, role, isOnline }) => {
      const selectedThread = selectedThreadRef.current || thread?.selectedThread;
      const customerId = getParticipantId(selectedThread?.user);
      const writerId = getParticipantId(selectedThread?.writer);

      if (role === roles.USER && userId?.toString() === customerId) {
        setThreadPresence((current) => ({
          ...current,
          customer: { userId: customerId, isOnline },
        }));
      }

      if (role === roles.WRITER && userId?.toString() === writerId) {
        setThreadPresence((current) => ({
          ...current,
          writer: { userId: writerId, isOnline },
        }));
      }
    };

    socket.on("presence:thread", handleThreadPresence);
    socket.on("presence:changed", handlePresenceChanged);

    return () => {
      socket.off("presence:thread", handleThreadPresence);
      socket.off("presence:changed", handlePresenceChanged);
    };
  }, [thread?.selectedThread?._id]);


  // client socket listening for incoming messages or errors
  useEffect(() => {
    socket.on('messageResponse', (data) => {
      appendMessageToRelatedChat(data);
    });

    socket.on("messageEdited", (data) => {
      chatRef.current = chatRef.current?.map((message) => (
        message?._id?.toString() === data?._id?.toString()
          ? { ...message, content: data?.content }
          : message
      )) || [];
      threasdsRef.current = threasdsRef.current?.map((thread) => (
        thread?.lastMessage?._id?.toString() === data?._id?.toString()
          ? {
              ...thread,
              lastMessage: {
                ...thread.lastMessage,
                content: data?.content,
              },
            }
          : thread
      )) || [];
      dispatch(updateThreadMessage({
        messageId: data?._id,
        threadId: data?.roomId,
        content: data?.content,
      }));
    });

    socket.on("messageDeleted", (data) => {
      chatRef.current = chatRef.current?.filter(
        (message) => message?._id?.toString() !== data?._id?.toString()
      ) || [];
      threasdsRef.current = threasdsRef.current?.map((item) => (
        item?._id?.toString() === data?.threadId?.toString()
          ? { ...item, lastMessage: data?.lastMessage || null }
          : item
      )) || [];
      dispatch(removeThreadMessage({
        messageId: data?._id,
        threadId: data?.threadId,
        lastMessage: data?.lastMessage,
      }));
    });

    socket.on("thread-read-response", (data) => {
      updateThreadReadState(data);
    });

    socket.on("error", (error) => {
      console.log("Server error:", error);
      showToast("error", error?.message || "Message could not be sent. Please try again.");
    });

    return () => {
      socket.off("messageResponse");
      socket.off("messageEdited");
      socket.off("messageDeleted");
      socket.off("thread-read-response");
      socket.off("error");
    };
  }, [socket]);

  /*
   *
   * =====================  CHATTING RELATED FUNCTION DEFINITIONS  =============================================================================
   *
   */

  // method to be used to swap the threads to top of the threads list when it getting new incoming messages & increment unread count locally
  const swapThreads = (prevThreads, roomId, userId) => {
    return new Promise(async (resolve, reject) => {
      let targetIndex = -1;
      let prev = prevThreads ? prevThreads : [];

      for (let i = 0; i < prev.length; i++) {
        if (prev[i]?._id?.toString() === roomId?.toString()) {
          targetIndex = i;
          break;
        }
      }

      // targetIndex not null means, the thread exist locally which is related to incoming recent message
      if (targetIndex > -1) {
        for (let j = targetIndex; j > 0; j--) {
          let temp = prev[j];
          prev[j] = prev[j - 1];
          prev[j - 1] = temp;
        }
      } else {
        let res = await doFetchThreadById({ roomId: roomId });

        // Using the splice() method to remove from the last nth index for n elements
        prev.splice(prev.length - 1, 1);

        if (res.success) {
          prev = [res.response.data, ...prev];
        } else {
          showToast("error", res?.error?.message || "Error Occurred!");
        }
      }
      return resolve([...prev]);
    });
  };

  // method to be used to append incoming message to related chat - as well as update the currently open chats if any
  const updateThreadReadState = ({ threadId, userId, lastReadTimestamp }) => {
    if (!threadId || !userId || !lastReadTimestamp) return;

    const applyReadState = (threadParam) => ({
      ...threadParam,
      lastRead: threadParam?.lastRead?.map((entry) => (
        entry?.userId?.toString() === userId?.toString()
          ? { ...entry, lastReadTimestamp, unreadCount: 0 }
          : entry
      )) || [],
    });

    if (selectedThreadRef.current?._id?.toString() === threadId?.toString()) {
      const updatedSelectedThread = applyReadState(selectedThreadRef.current);
      selectedThreadRef.current = updatedSelectedThread;
      dispatch(setSelectedThread(updatedSelectedThread));
    }

    const updatedThreads = threasdsRef.current?.map((threadParam) => (
      threadParam?._id?.toString() === threadId?.toString()
        ? applyReadState(threadParam)
        : threadParam
    )) || [];

    threasdsRef.current = updatedThreads;
    dispatch(setBulkThreads(updatedThreads));
  };

  const appendMessageToRelatedChat = async (newMessage) => {

    let threadPrev = selectedThreadRef.current;
    let chatPrev = chatRef.current;
    let prevUserId = userIdRef.current;

    //update the open chat if any
    if (newMessage?.roomId === threadPrev?._id) {
      dispatch(setThreadBulkChats([...chatPrev, { _id: newMessage?._id, sender: newMessage?.userId, content: newMessage?.text, attachments: newMessage?.attachments || [], replyTo: newMessage?.replyTo || null, createdAt: newMessage?.createdAt, isAdmin: newMessage?.isAdmin || false, senderModel: newMessage?.senderModel }]));
      chatRef.current = [...chatPrev, { _id: newMessage?._id, sender: newMessage?.userId, content: newMessage?.text, attachments: newMessage?.attachments || [], replyTo: newMessage?.replyTo || null, createdAt: newMessage?.createdAt, isAdmin: newMessage?.isAdmin || false, senderModel: newMessage?.senderModel }];

      if (newMessage?.userId?.toString() !== prevUserId?.toString()) {
        socket.emit("thread-read", {
          threadId: threadPrev?._id,
          userId: prevUserId,
        });
      }
    }

    //update the last message of matching thread and locally increment the incoming unread message count      
    let updatedThreads = threasdsRef.current.map((thread) => {
      if (newMessage?.roomId === thread?._id) {
        return {
          ...thread,
          lastMessage: { _id: newMessage?._id, sender: newMessage?.userId, content: newMessage?.text, attachments: [] },
          lastRead: thread?.lastRead?.map((item) => {
            if (item?.userId?.toString() === prevUserId?.toString() && thread?._id?.toString() !== threadPrev?._id?.toString()) {
              return { ...item, unreadCount: parseInt(item.unreadCount) + 1 };
            }
            return item;
          })
        };
      }
      return thread;
    });

    const w = await swapThreads(updatedThreads, newMessage?.roomId?.toString(), prevUserId);
    dispatch(setBulkThreads(w));
    threasdsRef.current = w;
    return w
  }

  // ================> Toggle functions <=======================

  // triggered when clicked on a thread located at left side bar
  const handleCardClick = async (threadParam) => {
    try {
      if (
        thread?.selectedFormType !== "message" &&
        thread?.selectedFormType !== "system"
      ) {
        dispatch(setSelectedFormType("message"));
      }
      dispatch(setSelectedThreadId(threadParam?._id));
      dispatch(setSelectedThreadRoomId(threadParam?.roomId));
      dispatch(setSelectedPage(pagination.page));
      // need t fetch the associated order with room id
      if (thread?.selectedThreadRoomId !== threadParam?.roomId) {
        dispatch(clearThreadChats());
        const res = await doFetchUserOrderByRoomId({
          roomId: threadParam?.roomId || "",
        });
        if (!res?.success) {
          showToast(
            "error",
            res?.error?.message || "Error occurred during fetching Data"
          );
        } else {
          dispatch(setSelectedFormType(res?.response?.data?.status === orderStatus.PENDING_PAYMENT ? "system" : "message"));
        }
      }
    } catch (error) {
      showToast("error", "Error occurred during fetching Data");
    }

    dispatch(setSelectedThread(threadParam));
    selectedThreadRef.current = threadParam;
  };

  const handleButtonClick = (formType) => {
    dispatch(setSelectedFormType(formType));
  };

  const toggleSection = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleViewOrder = () => {
    dispatch(setSelectedFormType("view-order"));
  };

  const handleReviewOrder = () => {
    dispatch(setSelectedFormType("review-order-latest"));
  };

  const handleAcceptOrder = () => {
    dispatch(setSelectedFormType("accept-order"));
  };

  const handleMakeExtraPayment = () => {
    dispatch(setSelectedFormType(canTipWriter(thread?.selectedOrder) ? "tip-writer" : "make-extra-payment"))
  }

  const renderDeliveryNotice = () => {
    const deliverables = getLatestDeliverables(thread?.selectedOrder);
    const hasEditorReview = hasEditorReviewDeliverable(thread?.selectedOrder);

    return (
      <div className="mx-[20px] mt-[18px] rounded-[8px] border border-medium-gray bg-white p-3 font-inter">
        <div className="flex items-center justify-between gap-3">
          <span className="font-semibold text-secondary">Delivered</span>
          {hasEditorReview && (
            <span className="rounded-full bg-light-blue px-2.5 py-1 text-[11px] font-semibold text-blue-shades">
              Editor reviewed
            </span>
          )}
        </div>
        <p className="mt-2 text-[12px] font-medium leading-4 text-gray">
          {deliverables.length} file{deliverables.length === 1 ? "" : "s"} available
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex h-[28px] items-center justify-center gap-1.5 rounded-full border border-[#BFD7FF] bg-[#EEF6FF] px-3 font-inter text-[11px] font-semibold leading-4 text-[#1D4ED8] transition-colors duration-200 hover:border-[#93C5FD] hover:bg-[#DBEAFE]"
            onClick={handleReviewOrder}
          >
            View Delivered Files
            <FaDownload className="h-4 w-4" />
          </button>
          {hasEditorReview && (
            <button
              type="button"
              className="inline-flex h-[28px] items-center justify-center gap-1.5 rounded-full border border-[#BFD7FF] bg-white px-3 font-inter text-[11px] font-semibold leading-4 text-[#1D4ED8] transition-colors duration-200 hover:border-[#93C5FD] hover:bg-[#DBEAFE]"
              onClick={handleReviewOrder}
            >
              View editor review
              <FaCheck className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  /**
   * 
   * ============================= DATA FETCHING OPERATIONS RELATED FUNCTIONS ======================================================
   * 
   */

  // load more threads
  const loadMoreThreads = () => {
    if (thread?.selectedPage) {
      setPagination({ ...pagination, page: thread?.selectedPage + 1 });
      dispatch(setSelectedPage(null));
    } else {
      setPagination({ ...pagination, page: pagination.page + 1 });
    }
  };


  // if room id and thread id query parameters does not exists
  // refetch latest selected thread object after refresh/close
  const refetchSelectedThreadAndOrder = async () => {
    if (thread.selectedThreadRoomId && !thread.selectedOrder) {
      const result = await doFetchUserOrderByRoomId({
        roomId: thread.selectedThreadRoomId || "",
      });
      if (!result?.success) {
        showToast("error", result?.error?.message);
      } else {
        const suggestedFormType = result?.response?.data?.status === orderStatus.PENDING_PAYMENT ? thread?.selectedFormType === "message" ? "system" : thread?.selectedFormType : thread?.selectedFormType;
        dispatch(setSelectedFormType(suggestedFormType));
      }
    }

    if (
      thread.selectedThreadId &&
      !thread.selectedThread &&
      thread.selectedPage
    ) {
      const result = await doFetchUserThreads({
        page: 0,
        size: pagination.size * thread.selectedPage,
      });
      if (result.success) {
        threasdsRef.current = result?.response?.data?.threads;
        setPagination({
          ...pagination,
          page: result?.response?.data?.threads?.length / SIZE,
        });
        setTotalNumOfRecords(
          result.response?.data?.pagination?.totalDocuments
        );
        for (const element of result?.response?.data?.threads) {
          if (
            element?._id?.toString() === thread?.selectedThreadId?.toString()
          ) {
            dispatch(setSelectedThread(element));
            selectedThreadRef.current = element;
          }
        }
      } else {
        showToast("error", result?.error?.message);
      }
    }

    if (
      thread.selectedThreadId &&
      !selectedThreadRef.current &&
      !thread.selectedPage
    ) {
      const result = await doFetchThreadById({ roomId: thread.selectedThreadId });
      if (result.success) {
        dispatch(setSelectedThread(result?.response?.data));
        selectedThreadRef.current = result?.response?.data;
      } else {
        showToast("error", result?.error?.message || "Error Occurred!");
      }
    }
  };

  // if room id and thread id query parameters exists
  // refetch latest selected thread object after refresh/close from query parameters
  const refetchSelectedThreadAndOrderFromQueryParams = async () => {
    // set necessary selected properties
    dispatch(setSelectedThreadId(threadIdSearchParam));
    dispatch(setSelectedThreadRoomId(roomIdSearchParam));

    // fetching the selected order by the room id
    const result = await doFetchUserOrderByRoomId({
      roomId: roomIdSearchParam || "",
    });
    if (result?.success) {
      const suggestedFormType = result?.response?.data?.status === orderStatus.PENDING_PAYMENT ? thread?.selectedFormType === "message" ? "system" : thread?.selectedFormType : thread?.selectedFormType;
      dispatch(setSelectedOrder(result.response?.data));
      dispatch(setSelectedFormType(suggestedFormType));
    } else {
      showToast("error", result?.error?.message);
    }

    // fetching the selected thread
    let res = await doFetchThreadById({ roomId: threadIdSearchParam });
    if (res.success) {
      dispatch(setSelectedThread(res?.response?.data));
      selectedThreadRef.current = res?.response?.data;
    } else {
      showToast("error", res?.error?.message || "Error Occurred!")
    }
  };

  // Fetch all user threads
  const fetchAllUserThreads = async () => {
    try {
      const result = await doFetchUserThreads({
        page: pagination.page,
        size: pagination.size,
      });

      if (result.success) {
        threasdsRef.current = [
          ...thread?.threads,
          ...result.response?.data?.threads,
        ];
        setTotalNumOfRecords(result.response?.data?.pagination?.totalDocuments);

        if (
          thread.selectedOrder &&
          thread.selectedThread &&
          !thread.selectedPage
        ) {
          dispatch(setSelectedPage(pagination.page));
        }
      }

      if (!result.success) {
        showToast("error", result.error?.message);
      }
    } catch {
      showToast("error", "Error occurred during fetching threads.");
    }
  };

  return (
    <div className="px-4 pt-4 flex flex-col lg:flex-row h-screen min-w-[340px] overflow-auto">
      {/* left sidebar */}
      {!roomIdSearchParam && !threadIdSearchParam && (
        <div
          className={`${isScreenLarge
              ? "min-w-[286px] h-screen static overflow-hidden"
              : isSidebarOpen
                ? "absolute w-[286px] h-full translate-x-0 bg-white shadow-lg left-0 top-0  z-50"
                : "absolute w-[286px] overflow-hidden -translate-x-full bg-white left-0 top-0  z-50"
            } flex flex-col items-center max-h- h-full justify-start transition-transform duration-300 ease-in-out`}
        >
          {!isScreenLarge && (
            <button
              className="text-medium-gray pt-8 absolute top-4 right-4"
              onClick={() => setIsSidebarOpen(false)}
            >
              <IoClose className="h-6 w-6 text-primary " />
            </button>
          )}
          <div
            className={`h-full ${isSidebarOpen ? "block overflow-auto" : "block"
              } flex flex-col items-center mt-[24px] `}
          >
            {isScreenLarge && (
              <ArrowButton
                text={`${isScreenSmall ? "Back to Dashboard" : "Back "}`}
	                onClick={() => {
	                  if(location.state?.backRedirect){
	                    navigate(`/${location.state?.backRedirect}`, { replace: true });
	                  }else{
	                    navigate("/dashboard", { replace: true });
	                  }
	                }}
                width="auto"
                height="44px"
                iconDirection="left"
                iconPosition="before"
                classname="mb-[25px]"
              />
            )}

            {/* Bordered Container */}
            <div
              className={`w-[286px] h-full flex flex-col flex-grow items-center justify-start ${!isScreenLarge
                  ? ""
                  : "border border-medium-gray rounded-[12px] max-h-full"
                }  py-[24px] overflow-hidden`}
            >
              {/* Fixed Topic */}
              <div className="flex flex-col items-start w-full mb-[24px]">
                <div className="flex items-start w-[240px] ml-[24px]">
                  <div className="flex space-x-4 justify-start items-center h-[40px] ">
                    <img src={logo} alt="" className="h-[40px] w-auto" />
                    <h1 className="font-roboto font-semibold text-primary text-[18px] leading-5">
                      Your Orders
                    </h1>
                  </div>
                </div>
              </div>

              {/* Fixed Search Bar */}
              {/* <div className="flex items-center font-inter bg-soft-gray border border-medium-gray rounded-[5px] p-2 w-[240px] mb-[24px]">
                <IoSearch className="text-gray w-6 h-6" />
                <input
                  type="text"
                  placeholder="Search"
                  className="ml-2 w-full bg-transparent text-gray text-[15px] leading-[20px] placeholder-gray focus:outline-none"
                />
              </div> */}

              {/* Scrollable Tutorial Cards Section */}
              {!(isFetchingUserThreads && thread?.threads?.length < 1) &&
                (
                  <div
                    className={`flex flex-wrap gap-4 pt-4 justify-center overflow-y-auto scrollbar-none ${isScreenLarge
                        ? "max-h-[calc(100vh-240px)]"
                        : "max-h-[calc(100vh-52px)]"
                      }`}
                  >
                    {thread?.threads?.map((threadItem, index) => (
                      <TutorialCard
                        key={threadItem?._id}
                        id={threadItem?.roomId}
                        title={threadItem?.subject}
                        // subtitle={`${index}`}
                        count={getUnreadMessagesCount(
                          threadItem,
                          tokenPayload?._id || null
                        )}
                        price={currencyFormatter.format(threadItem?.price || 0) || "$ 00.00"}
                        date={threadItem?.createdAt}
                        status={threadItem?.orderStatus}
                        onClick={() => handleCardClick(threadItem)}
                        isActive={
                          thread?.selectedThreadId?.toString() ===
                          threadItem?._id?.toString()
                        }
                      />
                    ))}
                    {!(!isFetchingUserThreads && thread?.threads?.length < 1) &&
                      (
                        <button
                          onClick={() => {
                            loadMoreThreads();
                          }}
                          className={twMerge(
                            "bg-light-blue text-blue-shades px-[8px] py-[8px] gap-[10px] leading-[19.36px] w-[120px] h-[44px] rounded-[5px] font-inter",
                            thread.threads?.length >= totalNumOfRecords
                              ? "bg-slate-100 text-slate-200"
                              : "bg-light-blue text-blue-shades"
                          )}
                          disabled={thread.threads?.length >= totalNumOfRecords}
                        >
                          {isFetchingUserThreads ? (
                            <div className="flex items-center justify-center">
                              <ClipLoader
                                loading={isFetchingUserThreads}
                                speedMultiplier={0.5}
                                size={22}
                                color="#60a5fa"
                              />
                            </div>
                          ) : (
                            "Load More"
                          )}
                        </button>
                      )
                    }
                  </div>
                )
              }
              {isFetchingUserThreads && thread?.threads?.length < 1 &&
                (
                  <div className="flex flex-col flex-grow justify-center items-center w-full h-[100]">
                    <ClipLoader
                      loading={isFetchingUserThreads && thread?.threads?.length < 1}
                      speedMultiplier={0.5}
                      size={82}
                      color="#60a5fa"
                    />
                  </div>
                )
              }
              {!isFetchingUserThreads && thread?.threads?.length < 1 &&
                (
                  <div className="flex flex-col flex-grow justify-center items-center w-full h-[100]">
                    <div className="flex flex-grow items-center justify-center">
                      <img src={waiting_two} alt="" />
                    </div>
                  </div>
                )
              }
            </div>
          </div>
        </div>
      )}

      {/* Middle Section */}
      <div
        className={`flex flex-col h-full lg:h-auto  flex-grow  mx-[20px] xl:mx-[40px] justify-start mt-0 lg:mt-[24px] mb-[40px]`}
      >
        {extraPaymentNotice && (
          <div className={`mb-4 rounded-lg border px-4 py-3 text-sm font-medium leading-5 ${extraPaymentNotice.classes}`} role="status">
            {extraPaymentNotice.text}
          </div>
        )}
        {/* Back button & thread button */}
        {!isScreenLarge && (
          <div className="flex flex-row z-0 w-full">
            {/* First Button  */}
            {!roomIdSearchParam && !threadIdSearchParam && (
              <button
                className=" sm:flex hidden flex-row items-center justify-center space-x-2 z-50 mb-4 h-[40px] p-2 bg-blue-shades text-white rounded"
                style={{ width: "234px" }} // Set a fixed width for the button
                onClick={() => setIsSidebarOpen(true)}
              >
                <FaRegMessage className="sm:flex hidden h-[24px] w-[24px]" />
                <p className="w-full sm:flex hidden border-l px-2 font-inter text-white font-medium">
                  Open Message Thread
                </p>
              </button>
            )}

            {!roomIdSearchParam && !threadIdSearchParam && (
              <button
                className="flex sm:hidden flex-row  items-center justify-center space-x-4 z-50 mb-4 h-[40px] p-2 bg-blue-shades text-white rounded"
                style={{ width: "40px" }} // Set a fixed width for the button
                onClick={() => setIsSidebarOpen(true)}
              >
                <HiMenu className="flex sm:hidden h-[30px] w-[30px]" />
              </button>
            )}

            {/* Inner Div */}
            <div
              className={twMerge(
                "flex-grow",
                !roomIdSearchParam && !threadIdSearchParam ? "ml-4" : ""
              )}
            >
              <ArrowButton
                text={`${isScreenSmall ? "Back to Dashboard" : "Back "}`}
	                onClick={() => {
	                  if(location.state?.backRedirect){
	                    navigate(`/${location.state?.backRedirect}`, { replace: true });
	                  }else{
	                    navigate("/dashboard", { replace: true });
	                  }
	                }}
                width="w-full"
                height="40px"
                iconDirection="left"
                iconPosition="before"
                classname="mb-[25px]"
              />
            </div>
          </div>
        )}

        {/* Tab buttons for small screens */}
        <div className="lg:hidden flex flex-row ml-0 mb-0 mt-2 font-roboto text-level-4 ">
          <button
            onClick={() => {
              handleButtonClick("message");
            }}
            className={twMerge(
              `py-2 w-full border border-medium-gray rounded-tl-[5px]`,
              thread?.selectedFormType === "message"
                ? "bg-light-blue text-blue-shades"
                : "text-gray"
            )}
            disabled={thread?.selectedOrder?.status === "PENDING_PAYMENT"}
          >
            Messages
          </button>
          <button
            onClick={() => {
              handleButtonClick("system");
            }}
            className={twMerge(
              `py-2 w-full border  border-medium-gray rounded-tr-[5px]`,
              thread?.selectedFormType !== "message" &&
                thread?.selectedFormType !== "Tab3"
                ? "text-blue-shades bg-light-blue"
                : "text-gray bg-white"
            )}
          >
            Notifications
          </button>
          <button
            onClick={() => {
              handleButtonClick("Tab3");
            }}
            className={twMerge(
              `py-2 w-full border  border-medium-gray rounded-tr-[5px]`,
              thread.selectedFormType === "Tab3"
                ? "text-blue-shades bg-light-blue"
                : "text-gray bg-white"
            )}
          >
            Details
          </button>
        </div>

        {thread?.selectedThread ? (
          <>
            <div className="flex flex-row w-full">
              <div className="flex flex-row">
                {/* back button */}
                {roomIdSearchParam && threadIdSearchParam && (
                  <button
                    className="hidden 1440-br:flex flex-row  items-center justify-center space-x-4 z-50 h-[40px] p-2 mr-3 bg-blue-shades text-white rounded"
                    style={{ width: "40px" }} // Set a fixed width for the button
                    // onClick={() => setIsSidebarOpen(true)}
	                    onClick={() => {
	                      if(location.state?.backRedirect){
	                        navigate(`/${location.state?.backRedirect}`, { replace: true });
	                      }else{
	                        navigate("/dashboard", { replace: true });
	                      }
	                    }}
                  >
                    <IoMdArrowRoundBack className="flex h-[30px] w-[30px]" />
                  </button>
                )}
              </div>
              <div className="flex flex-col w-full m-0 p-0">
                <div className="flex flex-row w-full ">
                  {/* Topic */}
                  <h2
                    className={`hidden lg:block font-roboto font-semibold text-primary text-level-2 leading-[20px] ${isScreenLarge ? "" : "ml-0"
                      }`}
                  >
                    {thread?.selectedThread?.roomId} <span> - </span>
                    {thread?.selectedOrder?.orderRequirements?.subject}{" "}
                    <span> </span>
                  </h2>
                </div>

                {/* Order Status */}
                <div
                  className={`hidden lg:flex flex-row items-center gap-2 mt-2 ${isScreenLarge ? "" : "ml-0"
                    }`}
                >
                  <div
                    className={`w-3 h-3 shrink-0 rounded-[29.27px] ${[
                        orderStatus.PENDING_PAYMENT,
                        orderStatus.IN_REVISION,
                        orderStatus.COMPLAINT_RAISED,
                      ].includes(thread?.selectedOrder?.status)
                        ? "bg-light-red"
                        : thread?.selectedOrder?.status ===
                          orderStatus.ORDER_IN_PROGRESS
                          ? "bg-[#06BCCC]"
                          : [
                            orderStatus.ORDER_DELIVERED,
                            orderStatus.COMPLETED,
                            orderStatus.RESOLVED,
                          ].includes(thread?.selectedOrder?.status)
                            ? "bg-blue-500"
                            : "bg-gray-500" // Default color if no status matches
                      } `}
                  ></div>
                  <p
                    className={`shrink-0 font-inter font-medium text-secondary text-level-5 leading-3`}
                  >
                    {customerOrderStatusLabel}
                  </p>
                  {orderStatusSummary && (
                    <p
                      title={orderStatusSummary}
                      className="ml-2 min-w-0 flex-1 truncate border-l border-medium-gray pl-3 font-inter text-[12px] font-medium leading-5 text-gray"
                    >
                      {orderStatusSummary}
                    </p>
                  )}
                  {!orderStatusSummary && (
                    <div className="flex flex-row flex-grow"></div>
                  )}
                  {selectedWriterId && (
                    <PresenceBadge label="Writer" isOnline={isSelectedWriterOnline} />
                  )}
                  {/* toggle button for system and normal messages */}

                  <div className=" hidden lg:flex shrink-0 items-center relative font-inter bg-soft-blue border border-blue-shades rounded-full w-[180px] h-[28px] font-[500]">
                    {/* Sliding background for active form */}
                    <div
                      className={`absolute rounded-full h-[28px] transition-all duration-300 ${thread?.selectedFormType === "message"
                          ? "bg-blue-shades left-0 w-[80px]"
                          : "bg-blue-shades left-[80px] w-[100px]"
                        }`}
                    ></div>

                    {/* Message Button */}
                    <button
                      onClick={() => handleButtonClick("message")}
                      className={twMerge(
                        `z-10 w-[80px] h-[23px] text-[12px] leading-[14.45px] cursor-pointer`,
                        thread?.selectedFormType === "message"
                          ? "text-white"
                          : "text-secondary"
                      )}
                      disabled={
                        thread?.selectedOrder?.status === "PENDING_PAYMENT"
                      }
                    >
                      Message
                    </button>

                    {/* Notifications Button */}
                    <button
                      onClick={() => handleButtonClick("system")}
                      className={twMerge(
                        `z-10 w-[100px] h-[23px] text-[12px] leading-[14.45px] cursor-pointer`,
                        thread?.selectedFormType !== "message"
                          ? "text-white"
                          : "text-secondary"
                      )}
                    >
                      Notifications
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {isLargeScreen || thread?.selectedFormType === "message" ? (
              <>
                <div className="flex relative border flex-grow border-medium-gray rounded-b-[12px] lg:rounded-[12px] p-4 overflow-hidden mb-12 lg:mb-0">
                  {/* Scrollable Messages Container */}
                  <div
                    className="h-[100%] w-full overflow-auto scrollbar-none "
                    style={{ maxHeight: "calc(100% - 0px)" }}
                  >
                    <MessageList
                      socket={socket}
                      roleRef={roleRef}
                      userIdRef={userIdRef}
                      selectedThreadRef={selectedThreadRef}
                      threasdsRef={threasdsRef}
                      chatRef={chatRef}
                      isFetchUserOrderByRoomId={isFetchUserOrderByRoomId}
                      isFetchThreadById={isFetchThreadById}
                      roomIdSearchParam={roomIdSearchParam}
                      threadIdSearchParam={threadIdSearchParam}
                      refetchSelectedThreadAndOrderFromQueryParams={refetchSelectedThreadAndOrderFromQueryParams}
                      refetchSelectedThreadAndOrder={refetchSelectedThreadAndOrder}
                    />
                  </div>
                </div>{" "}
              </>
            ) : thread?.selectedFormType !== "message" &&
              thread?.selectedFormType !== "Tab3" ? (
              <>
                <div
                  className="overflow-auto scrollbar-none border border-medium-gray"
                  style={{ maxHeight: "calc(100% - 0px)" }}
                >
                  <MessageList
                    socket={socket}
                    roleRef={roleRef}
                    userIdRef={userIdRef}
                    selectedThreadRef={selectedThreadRef}
                    threasdsRef={threasdsRef}
                    chatRef={chatRef}
                    isFetchUserOrderByRoomId={isFetchUserOrderByRoomId}
                    isFetchThreadById={isFetchThreadById}
                    roomIdSearchParam={roomIdSearchParam}
                    threadIdSearchParam={threadIdSearchParam}
                    refetchSelectedThreadAndOrderFromQueryParams={refetchSelectedThreadAndOrderFromQueryParams}
                    refetchSelectedThreadAndOrder={refetchSelectedThreadAndOrder}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="h-full border border-medium-gray rounded-tr-[12px] rounded-b-[12px] lg:rounded-[12px] px-2 py-3 sm:p-4 mb-12 lg:mb-0 overflow-auto">
                  <div className="mt-[22px]">
                    {![
                      // orderStatus.ORDER_DELIVERED,
                      orderStatus.RESOLVED,
                      orderStatus.COMPLETED,
                      orderStatus.CANCELLED,
                      orderStatus.PENDING_PAYMENT,
                      orderStatus.COMPLAINT_RAISED,
                      orderStatus.IN_REVISION,
                    ].includes(thread?.selectedOrder?.status) && (
                        <CountdownTimer
                          deliveryDate={
                            thread?.selectedOrder?.orderRequirements?.deadline
                          }
                          deliveredDate={thread?.selectedOrder?.deliveredDate}
                          autoAcceptAt={thread?.selectedOrder?.autoAcceptAt}
                          status={thread?.selectedOrder?.status}
                          handleReviewOrder={handleReviewOrder}
                        />
                      )}
                  </div>

                  <OrderDetails
                    tutorial={{
                      ...thread?.selectedThread,
                      deadline:
                        thread?.selectedOrder?.orderRequirements?.deadline,
                    }}
                    isExpanded={isExpanded}
	                    toggleSection={toggleSection}
	                    handleViewOrder={handleViewOrder}
	                    handleAcceptOrder={handleAcceptOrder}
	                    handleMakeExtraPayment = {handleMakeExtraPayment}
	                  >
                    {thread?.selectedThread &&
                      [
                        orderStatus.ORDER_DELIVERED,
                        orderStatus.COMPLETED,
                        orderStatus.COMPLAINT_RAISED,
                        orderStatus.RESOLVED,
                      ].includes(thread?.selectedOrder?.status) && (
                        renderDeliveryNotice()
                      )}
                  </OrderDetails>

                  {thread?.selectedThread && (
                    <TrackOrder
                      trackOrder={thread?.selectedOrder?.orderProgress}
                      currentState={thread?.selectedOrder?.status}
                      isWriterAssigned={isWriterAssigned}
                    />
                  )}

                  <AssignedWriterProfileCard />
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* Default view */}
            <div className="flex flex-col h-full">
              <p
                className={` ${isScreenLarge ? "" : ""
                  } hidden lg:block font-roboto font-semibold text-primary text-level-2 leading-[20px] mb-[48px] `}
              >
                Select your Order
              </p>
              <div className="border flex-grow  flex-col flex items-center text-center justify-center border-medium-gray rounded-b-[12px] p-4 relative overflow-auto scrollbar-webkit ">
                {!thread?.selectedOrder && isFetchingUserThreads &&
                  <>
                    <div className="flex items-center justify-center w-full h-[100%]">
                      <ClipLoader
                        loading={true}
                        speedMultiplier={0.5}
                        size={82}
                        color="#60a5fa"
                      />
                    </div>
                  </>
                }
                {!roomIdSearchParam && !threadIdSearchParam && !isFetchingUserThreads &&
                  <>
                    <img src={waiting} alt="" />
                    <p className="font-roboto font-semibold text-primary text-level-2 leading-[20px] mb-[24px] ">
                      Select one order to continue
                    </p>
                    <p className="font-inter font-normal text-level-4 text-gray max-w-[376px]">
                      Fill the catalog with Orders by adding your assignments. All
                      Orders are posted anonymously.
                    </p>
                    <ArrowButton
                      text="Do My Paper"
                      onClick={() => { navigate("/calculation") }}
                      width="auto"
                      height="44px"
                      iconDirection="right"
                      iconPosition="after"
                      classname="mt-[24px]"
                    />
                  </>
                }
              </div>
            </div>
          </>
        )}
      </div>

      {/* right Section */}
      <div className=" flex-col lg:block min-w-[286px] max-w-[286px] hidden justify-start items-center mt-[24px] border mb-[40px] border-medium-gray rounded-[5px] overflow-y-auto pb-6">
        <div className="">
          {" "}
          {thread?.selectedThread ? (
            <>
              <div className="mt-[22px]">
                {![
                  // orderStatus.ORDER_DELIVERED,
                  orderStatus.RESOLVED,
                  orderStatus.COMPLETED,
                  orderStatus.CANCELLED,
                  orderStatus.PENDING_PAYMENT,
                  orderStatus.COMPLAINT_RAISED,
                  orderStatus.IN_REVISION,
                ].includes(thread?.selectedOrder?.status) && (
                    <CountdownTimer
                      deliveryDate={
                        thread?.selectedOrder?.orderRequirements?.deadline
                      }
                      deliveredDate={thread?.selectedOrder?.deliveredDate}
                      autoAcceptAt={thread?.selectedOrder?.autoAcceptAt}
                      status={thread?.selectedOrder?.status}
                      handleReviewOrder={handleReviewOrder}
                    />
                  )}
              </div>

              <OrderDetails
                tutorial={{
                  ...thread?.selectedThread,
                  deadline: thread?.selectedOrder?.orderRequirements?.deadline,
                }}
                isExpanded={isExpanded}
	                toggleSection={toggleSection}
	                handleViewOrder={handleViewOrder}
	                handleMakeExtraPayment = {handleMakeExtraPayment}
	                showDeliveryActions={false}
	              >
                {thread?.selectedThread &&
                  [
                    orderStatus.ORDER_DELIVERED,
                    orderStatus.COMPLETED,
                    orderStatus.COMPLAINT_RAISED,
                    orderStatus.RESOLVED,
                  ].includes(thread?.selectedOrder?.status) && (
                        renderDeliveryNotice()
                      )}
              </OrderDetails>
              
              {thread?.selectedThread && (
                <TrackOrder
                  trackOrder={thread?.selectedOrder?.orderProgress}
                  currentState={thread?.selectedOrder?.status}
                  isWriterAssigned={isWriterAssigned}
                />
              )}

              <AssignedWriterProfileCard />
            </>
          ) : (
            <div className="flex flex-col relative w-full">
              <h1 className="absolute top-4 left-4 font-roboto font-semibold text-[18px] leading-5 text-primary ">
                Order Details
              </h1>
              <div className="flex flex-grow items-center justify-center pt-[100px] md:pt-[160px] lg:pt-[200px] 2xl:pt-[260px]">
                <img src={waiting_two} alt="" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Thread;
